import React, { useEffect, useState, useCallback } from "react";
import { api, fmtIDR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { TrendingUp, ShoppingBag, Receipt, Clock, CalendarDays, Trophy, Coins, Package, Utensils, ConciergeBell, Landmark, Download, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";

const RANGES = [["today", "Hari Ini"], ["yesterday", "Kemarin"], ["7d", "7 Hari"], ["month", "Bulan Ini"], ["year", "Tahun Ini"], ["custom", "Per Tanggal"], ["all", "Semua"]];
const RANGE_LABEL = Object.fromEntries(RANGES);

export function SalesReport({ branchId, owner = false, branches = [] }) {
  const [rng, setRng] = useState("today");
  const [bid, setBid] = useState(branchId || "");
  const [dstart, setDstart] = useState("");
  const [dend, setDend] = useState("");
  const [data, setData] = useState(null);
  const [cid, setCid] = useState("");

  const load = useCallback(async () => {
    if (rng === "custom" && (!dstart || !dend)) { setData(null); return; }
    const params = { rng, branch_id: owner ? bid : branchId || "", start: dstart, end: dend, cashier_id: cid };
    const { data } = await api.get("/reports/analytics", { params });
    setData(data);
  }, [rng, bid, branchId, owner, dstart, dend, cid]);
  useEffect(() => { load(); }, [load]);

  const maxSeries = data ? Math.max(1, ...data.series.map((s) => s.total)) : 1;
  const maxHour = data ? Math.max(1, ...data.by_hour.map((h) => h.count)) : 1;

  const branchLabel = () => {
    if (owner) return bid ? (branches.find((b) => b.id === bid)?.name || "Cabang") : "Semua Cabang";
    return branches.find((b) => b.id === branchId)?.name || "Cabang";
  };
  const periodLabel = () => (rng === "custom" ? `${dstart} s/d ${dend}` : (RANGE_LABEL[rng] || rng));
  const cashierLabel = () => (cid ? (data?.cashiers?.find((c) => c.cashier_id === cid)?.cashier_name || "Kasir") : "Semua Kasir");

  const downloadPDF = async () => {
    if (!data) return;
    let rep = data;
    try {
      const params = { rng, branch_id: owner ? bid : branchId || "", start: dstart, end: dend, cashier_id: cid, full: true };
      const res = await api.get("/reports/analytics", { params });
      rep = res.data;
    } catch (e) { /* fallback to on-screen data */ }
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210; let y = 16;
    doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("DA LE TANG HuoGuo", 14, y); y += 6;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text("Laporan Penjualan & Pajak", 14, y); y += 6;
    doc.setFontSize(9);
    doc.text(`Cabang : ${branchLabel()}`, 14, y); y += 5;
    doc.text(`Kasir : ${cashierLabel()}`, 14, y); y += 5;
    doc.text(`Periode : ${periodLabel()}`, 14, y); y += 5;
    doc.text(`Dicetak : ${new Date().toLocaleString("id-ID")}`, 14, y); y += 7;

    doc.setDrawColor(180); doc.line(14, y, W - 14, y); y += 7;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Ringkasan Pendapatan", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const rows = [
      ["Pendapatan Penjualan Makanan", fmtIDR(rep.food_revenue || 0)],
      ["Service Charge", fmtIDR(rep.service_revenue || 0)],
      ["Pajak PB1", fmtIDR(rep.tax_revenue || 0)],
    ];
    rows.forEach(([k, v]) => { doc.text(k, 14, y); doc.text(v, W - 14, y, { align: "right" }); y += 6; });
    doc.setDrawColor(180); doc.line(14, y - 2, W - 14, y - 2);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL PENDAPATAN", 14, y + 3); doc.text(fmtIDR(rep.total_revenue), W - 14, y + 3, { align: "right" });
    y += 7; doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Jumlah Transaksi : ${rep.transaction_count}`, 14, y + 3); y += 12;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Metode Pembayaran", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    if (rep.by_method.length === 0) { doc.text("-", 14, y); y += 6; }
    rep.by_method.forEach((m) => { doc.text(`${m.method.toUpperCase()} (${m.count}x)`, 14, y); doc.text(fmtIDR(m.total), W - 14, y, { align: "right" }); y += 6; });
    y += 4;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Daftar Transaksi (${rep.transactions.length})`, 14, y); y += 6;
    doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("No Struk", 14, y); doc.text("Waktu", 64, y); doc.text("Kasir", 108, y); doc.text("Bayar", 150, y); doc.text("Total", W - 14, y, { align: "right" }); y += 4;
    doc.line(14, y, W - 14, y); y += 4;
    doc.setFont("helvetica", "normal");
    rep.transactions.forEach((t) => {
      if (y > 285) { doc.addPage(); y = 16; }
      doc.text(String(t.receipt_no), 14, y);
      doc.text(new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), 64, y);
      doc.text(String(t.cashier_name || "-").slice(0, 20), 108, y);
      doc.text((t.payment_method || "").toUpperCase(), 150, y);
      doc.text(fmtIDR(t.total), W - 14, y, { align: "right" });
      y += 5;
    });
    doc.save(`Laporan_${branchLabel().replace(/\s+/g, "_")}_${periodLabel().replace(/[\s/]+/g, "_")}.pdf`);
  };

  const downloadCashierPDF = () => {
    if (!data?.cashiers) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210; let y = 16;
    doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("DA LE TANG HuoGuo", 14, y); y += 6;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text("Ringkasan Pendapatan per Kasir", 14, y); y += 6;
    doc.setFontSize(9);
    doc.text(`Cabang : ${branchLabel()}`, 14, y); y += 5;
    doc.text(`Periode : ${periodLabel()}`, 14, y); y += 5;
    doc.text(`Dicetak : ${new Date().toLocaleString("id-ID")}`, 14, y); y += 7;
    doc.setDrawColor(180); doc.line(14, y, W - 14, y); y += 7;

    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("Kasir", 14, y); doc.text("Jml Transaksi", 120, y, { align: "right" }); doc.text("Total Pendapatan", W - 14, y, { align: "right" }); y += 4;
    doc.line(14, y, W - 14, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    data.cashiers.forEach((c) => {
      if (y > 285) { doc.addPage(); y = 16; }
      doc.text(String(c.cashier_name || "-").slice(0, 40), 14, y);
      doc.text(String(c.count), 120, y, { align: "right" });
      doc.text(fmtIDR(c.total), W - 14, y, { align: "right" });
      y += 6;
    });
    doc.setDrawColor(180); doc.line(14, y - 2, W - 14, y - 2);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL", 14, y + 3);
    doc.text(String(data.cashiers.reduce((s, c) => s + c.count, 0)), 120, y + 3, { align: "right" });
    doc.text(fmtIDR(data.cashiers.reduce((s, c) => s + c.total, 0)), W - 14, y + 3, { align: "right" });
    doc.save(`Ringkasan_Kasir_${branchLabel().replace(/\s+/g, "_")}_${periodLabel().replace(/[\s/]+/g, "_")}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid="sales-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5 rounded-xl bg-neutral-100 p-1">
            {RANGES.map(([k, l]) => (
              <button key={k} data-testid={`report-range-${k}`} onClick={() => setRng(k)}
                className={`rounded-lg px-3 py-2 text-xs font-bold transition-all ${rng === k ? "bg-crimson text-white" : "text-muted-foreground hover:text-white"}`}>{l}</button>
            ))}
          </div>
          {rng === "custom" && (
            <div className="flex items-center gap-2" data-testid="report-custom-range">
              <Input type="date" data-testid="report-date-start" value={dstart} onChange={(e) => setDstart(e.target.value)} className="h-10 w-40 bg-neutral-100 border-neutral-200 text-xs text-neutral-900" />
              <span className="text-muted-foreground">—</span>
              <Input type="date" data-testid="report-date-end" value={dend} onChange={(e) => setDend(e.target.value)} className="h-10 w-40 bg-neutral-100 border-neutral-200 text-xs text-neutral-900" />
            </div>
          )}
        </div>
        {owner && (
          <Select value={bid || "all"} onValueChange={(v) => setBid(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-branch" className="h-10 w-56 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Cabang</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {data?.cashiers && (
          <Select value={cid || "all"} onValueChange={(v) => setCid(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-cashier" className="h-10 w-52 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kasir</SelectItem>
              {data.cashiers.map((c) => <SelectItem key={c.cashier_id || "none"} value={c.cashier_id || "none"}>{c.cashier_name} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {data && (
          <Button data-testid="report-download-pdf" onClick={downloadPDF} className="h-10 gap-2 bg-crimson font-bold text-white hover:bg-crimson/90">
            <Download size={16} /> Download PDF
          </Button>
        )}
      </div>

      {!data ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="report-empty-state">{rng === "custom" && (!dstart || !dend) ? "Pilih tanggal mulai & selesai." : "Memuat laporan…"}</div>
      ) : (
      <>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Metric icon={TrendingUp} label="Pendapatan" value={fmtIDR(data.total_revenue)} glow testid="report-revenue" />
        <Metric icon={ShoppingBag} label="Transaksi" value={data.transaction_count} testid="report-count" />
        <Metric icon={Receipt} label="Rata2/Struk" value={fmtIDR(data.avg_ticket)} />
        <Metric icon={Package} label="Item Terjual" value={data.items_sold} />
        <Metric icon={Clock} label="Jam Ramai" value={data.peak_hour} />
        <Metric icon={CalendarDays} label="Hari Ramai" value={data.peak_day} />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Metric icon={Utensils} label="Pendapatan Makanan" value={fmtIDR(data.food_revenue || 0)} testid="report-food-revenue" />
        <Metric icon={ConciergeBell} label="Service Charge" value={fmtIDR(data.service_revenue || 0)} testid="report-service-revenue" />
        <Metric icon={Landmark} label="Pajak PB1" value={fmtIDR(data.tax_revenue || 0)} testid="report-tax-revenue" />
      </div>

      <Card className="card-surface rounded-2xl p-5" data-testid="cashier-summary-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-display font-bold text-neutral-900"><Users size={16} className="text-gold" /> Ringkasan per Kasir</div>
          {data.cashiers && data.cashiers.length > 0 && (
            <Button data-testid="cashier-summary-pdf" onClick={downloadCashierPDF} size="sm" variant="outline" className="h-8 gap-1.5 border-neutral-300 text-xs font-bold text-neutral-900 hover:bg-neutral-200">
              <Download size={14} /> Download PDF
            </Button>
          )}
        </div>
        {(!data.cashiers || data.cashiers.length === 0) ? <p className="text-sm text-muted-foreground">Belum ada transaksi.</p> : (
          <div className="space-y-2">
            {data.cashiers.map((c, i) => {
              const maxC = Math.max(1, ...data.cashiers.map((x) => x.total));
              return (
                <div key={c.cashier_id || `k${i}`} data-testid={`cashier-summary-row-${i}`} className="rounded-xl bg-neutral-100 p-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold text-white"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-crimson text-[11px] font-bold">{i + 1}</span>{c.cashier_name}</span>
                    <span className="font-mono-num text-sm font-bold text-gold">{fmtIDR(c.total)}</span>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{c.count} transaksi</span>
                    <span>Rata2 {fmtIDR(Math.round(c.total / (c.count || 1)))}/struk</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-neutral-100"><div className="h-1.5 rounded-full bg-gradient-to-r from-crimson to-gold" style={{ width: `${(c.total / maxC) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-4 font-display font-bold text-neutral-900">Grafik Penjualan</div>
        {data.series.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada transaksi pada periode ini.</p> : (
          <div className="flex h-48 items-end gap-1.5 overflow-x-auto pb-1">
            {data.series.map((s) => (
              <div key={s.label} className="flex min-w-[26px] flex-1 flex-col items-center gap-1" title={fmtIDR(s.total)}>
                <div className="w-full rounded-t bg-gradient-to-t from-crimson to-gold transition-all hover:opacity-80" style={{ height: `${(s.total / maxSeries) * 100}%`, minHeight: s.total > 0 ? 4 : 0 }} />
                <span className="whitespace-nowrap text-[9px] text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="card-surface rounded-2xl p-5">
          <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><Trophy size={16} className="text-gold" /> Item Terlaris (Kuantitas)</div>
          {rankList(data.top_by_qty, (t) => `${t.qty}x`)}
        </Card>
        <Card className="card-surface rounded-2xl p-5">
          <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><Coins size={16} className="text-gold" /> Item Terlaris (Nilai)</div>
          {rankList(data.top_by_value, (t) => fmtIDR(t.revenue))}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="card-surface rounded-2xl p-5">
          <div className="mb-3 font-display font-bold text-neutral-900">Metode Pembayaran</div>
          {data.by_method.length === 0 ? <p className="text-sm text-muted-foreground">-</p> :
            data.by_method.map((m) => (
              <div key={m.method} className="mb-2 flex justify-between text-sm">
                <span className="uppercase text-neutral-900">{m.method} <span className="text-[10px] text-muted-foreground">({m.count}x)</span></span>
                <span className="font-mono-num text-gold">{fmtIDR(m.total)}</span>
              </div>
            ))}
        </Card>
        <Card className="card-surface rounded-2xl p-5">
          <div className="mb-3 font-display font-bold text-neutral-900">Transaksi per Jam</div>
          <div className="flex h-28 items-end gap-[2px]">
            {data.by_hour.map((h) => (
              <div key={h.label} className="flex-1 rounded-t bg-crimson/70" style={{ height: `${(h.count / maxHour) * 100}%`, minHeight: h.count > 0 ? 3 : 0 }} title={`${h.label}:00 · ${h.count} transaksi`} />
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[9px] text-muted-foreground"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
        </Card>
      </div>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 font-display font-bold text-neutral-900">Daftar Transaksi ({data.transactions.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="report-transactions">
            <thead><tr className="border-b border-neutral-200 text-[11px] uppercase text-muted-foreground">
              <th className="py-2 pr-3">No Struk</th><th className="py-2 pr-3">Waktu</th><th className="py-2 pr-3">Kasir</th><th className="py-2 pr-3">Item</th><th className="py-2 pr-3">Bayar</th><th className="py-2 text-right">Total</th>
            </tr></thead>
            <tbody>
              {data.transactions.map((t) => (
                <tr key={t.id} className="border-b border-neutral-200">
                  <td className="py-2 pr-3 font-mono-num text-xs text-neutral-900">{t.receipt_no}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2 pr-3 text-xs text-neutral-900">{t.cashier_name || "-"}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{t.items.reduce((s, i) => s + i.qty, 0)} item</td>
                  <td className="py-2 pr-3 text-xs uppercase text-neutral-900">{t.payment_method}</td>
                  <td className="py-2 text-right font-mono-num text-gold">{fmtIDR(t.total)}</td>
                </tr>
              ))}
              {data.transactions.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Belum ada transaksi.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}

function rankList(items, fmt) {
  if (!items || items.length === 0) return <p className="text-sm text-muted-foreground">Belum ada data.</p>;
  const max = Math.max(1, ...items.map((i) => (fmt(i).includes("Rp") ? i.revenue : i.qty)));
  return (
    <div className="space-y-2">
      {items.map((t, i) => {
        const val = fmt(t).includes("Rp") ? t.revenue : t.qty;
        return (
          <div key={t.name}>
            <div className="mb-1 flex justify-between text-sm"><span className="text-neutral-900">{i + 1}. {t.name}</span><span className="font-mono-num text-gold">{fmt(t)}</span></div>
            <div className="h-1.5 rounded-full bg-neutral-100"><div className="h-1.5 rounded-full bg-gold" style={{ width: `${(val / max) * 100}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function Metric({ icon: Icon, label, value, glow, testid }) {
  return (
    <div className={`card-surface rounded-2xl p-4 ${glow ? "gold-glow" : ""}`}>
      <Icon className={glow ? "text-gold" : "text-crimson"} size={18} />
      <div data-testid={testid} className="mt-2 font-mono-num text-lg font-extrabold text-neutral-900">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
